import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ChevronDown, Check, Plus } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import AddWorkspaceModal from "../AddWorkspaceModal";
import betaAPI from "v2/api";
import showToast from "utils/toast";
import useWorkspaceLogo from "v2/hooks/useWorkspaceLogo";

const generateGradient = (name) => {
  const hash = [...name].reduce((acc, char) => acc + char.charCodeAt(0), 0);
  const hue1 = hash % 360;
  const hue2 = (hue1 + 40) % 360;
  return `linear-gradient(to right, hsl(${hue1}, 70%, 60%), hsl(${hue2}, 70%, 60%))`;
};

const getInitials = (name) =>
  name
    .split(" ")
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();

// Renders logo if available, gradient avatar otherwise.
// Each instance fetches independently but shares the module-level cache.
const WorkspaceAvatar = ({ workspace, size = "md" }) => {
  const { logoUrl } = useWorkspaceLogo(workspace.id);
  const dim =
    size === "sm"
      ? "h-7 w-7 rounded-lg text-[10px]"
      : "h-9 w-9 rounded-full text-xs";

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={workspace.name}
        className={`${
          size === "sm" ? "h-7 w-7 rounded-lg" : "h-9 w-9 rounded-full"
        } shrink-0 object-cover`}
      />
    );
  }
  return (
    <div
      className={`flex ${dim} shrink-0 items-center justify-center font-semibold text-white`}
      style={{ background: generateGradient(workspace.name) }}
    >
      {getInitials(workspace.name)}
    </div>
  );
};

const WorkspaceSelector = ({
  workspaces,
  selectedWorkspaceId,
  onWorkspaceChange,
  onWorkspaceCreated,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedWorkspace = workspaces.find(
    (w) => String(w.id) === String(selectedWorkspaceId)
  );
  const displayName =
    selectedWorkspace?.name || t("navigation.workspaceSelector.loading");

  const handleSelect = (workspaceId) => {
    if (String(workspaceId) !== String(selectedWorkspaceId)) {
      localStorage.setItem("selectedWorkspace", String(workspaceId));
      // Use navigate for URL-based routing, fall back to callback if provided
      navigate(`/v2/${workspaceId}`);
      onWorkspaceChange?.(String(workspaceId));
    }
    setOpen(false);
  };

  const handleCreateWorkspace = () => {
    setOpen(false);
    setShowCreateModal(true);
  };

  const handleCreateSubmit = async (data) => {
    try {
      const response = await betaAPI.createWorkspace({ body: data });
      showToast.success(t("navigation.workspaceSelector.created"));
      // Select the new workspace
      const newWorkspaceId = response.data?.id;
      if (newWorkspaceId) {
        localStorage.setItem("selectedWorkspace", String(newWorkspaceId));
        await onWorkspaceCreated?.();
        navigate(`/v2/${newWorkspaceId}`);
        onWorkspaceChange?.(String(newWorkspaceId));
      }
    } catch (err) {
      console.error("Failed to create workspace:", err);
      showToast.error(t("navigation.workspaceSelector.createFailed"));
      throw err; // Re-throw so modal knows it failed
    }
  };

  return (
    <div ref={dropdownRef} className="relative">
      {/* Trigger Button */}
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-3 rounded-lg px-2 py-1.5 transition duration-150 hover:bg-[#F5F5F5]"
      >
        {/* Workspace logo or initials avatar */}
        {selectedWorkspace ? (
          <WorkspaceAvatar workspace={selectedWorkspace} size="sm" />
        ) : (
          <div className="h-7 w-7 rounded-lg bg-[#E5E7EB]" />
        )}

        {/* Workspace info */}
        <div className="flex flex-col items-start">
          <span className="text-sm font-semibold text-gray-900">
            {displayName}
          </span>
        </div>

        {/* Chevron */}
        <motion.div
          animate={{ rotate: open ? 180 : 0 }}
          transition={{ duration: 0.15 }}
        >
          <ChevronDown size={16} className="text-gray-400" />
        </motion.div>
      </button>

      {/* Dropdown Panel */}
      <AnimatePresence>
        {open && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => setOpen(false)}
            />

            {/* Panel */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: -8 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: -8 }}
              transition={{ duration: 0.15 }}
              className="absolute left-0 top-full z-50 mt-2 w-[280px] overflow-hidden rounded-[14px] bg-white shadow-[0_4px_24px_rgba(0,0,0,0.12)]"
            >
              {/* Workspace List */}
              <div className="max-h-[300px] overflow-y-auto py-2">
                {workspaces.map((ws) => {
                  const isSelected =
                    String(ws.id) === String(selectedWorkspaceId);
                  return (
                    <div
                      key={ws.id}
                      onClick={() => handleSelect(ws.id)}
                      className="flex cursor-pointer items-center gap-3 px-4 py-2.5 transition duration-150 hover:bg-[#F9FAFB]"
                    >
                      {/* Avatar */}
                      <WorkspaceAvatar workspace={ws} />

                      {/* Info */}
                      <div className="flex flex-1 flex-col">
                        <span
                          className={`text-sm ${
                            isSelected
                              ? "font-semibold text-gray-900"
                              : "font-medium text-gray-700"
                          }`}
                        >
                          {ws.name}
                        </span>
                      </div>

                      {/* Checkmark */}
                      {isSelected && (
                        <Check size={18} className="shrink-0 text-[#DA7720]" />
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Footer - Create New */}
              <div className="border-t border-gray-100">
                <div
                  onClick={handleCreateWorkspace}
                  className="flex cursor-pointer items-center gap-3 px-4 py-3 transition duration-150 hover:bg-[#F9FAFB]"
                >
                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gray-100">
                    <Plus size={18} className="text-gray-500" />
                  </div>
                  <span className="text-sm font-medium text-gray-700">
                    {t("navigation.workspaceSelector.newWorkspace")}
                  </span>
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Create Workspace Modal */}
      <AddWorkspaceModal
        show={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateSubmit}
      />
    </div>
  );
};

export default WorkspaceSelector;
